import { Controller } from "@hotwired/stimulus"

// Bulk operations controller for content lists
// Enables multi-select and batch actions
export default class extends Controller {
  static targets = ["checkbox", "selectAll", "bulkActions", "selectedCount", "row"]
  static values = {
    submitUrl: String
  }

  connect() {
    this.updateBulkActionsVisibility()
  }

  // Toggle all checkboxes
  toggleAll(event) {
    const checked = event.target.checked
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = checked
      this.updateRowHighlight(checkbox)
    })
    this.updateBulkActionsVisibility()
  }

  // Single checkbox toggle
  toggle(event) {
    this.updateRowHighlight(event.target)
    this.updateSelectAllState()
    this.updateBulkActionsVisibility()
  }

  // Update row highlight based on selection
  updateRowHighlight(checkbox) {
    const row = checkbox.closest("tr, .content-card, .list-item")
    if (row) {
      row.classList.toggle("selected", checkbox.checked)
    }
  }

  // Update "select all" checkbox state
  updateSelectAllState() {
    if (!this.hasSelectAllTarget) return

    const total = this.checkboxTargets.length
    const checked = this.checkboxTargets.filter(cb => cb.checked).length

    if (checked === 0) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    } else if (checked === total) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    } else {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = true
    }
  }

  // Show/hide bulk actions bar
  updateBulkActionsVisibility() {
    const selectedCount = this.selectedIds.length

    if (this.hasBulkActionsTarget) {
      this.bulkActionsTarget.classList.toggle("visible", selectedCount > 0)
    }

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = selectedCount
    }
  }

  // Get array of selected IDs
  get selectedIds() {
    return this.checkboxTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value)
  }

  // Bulk publish action
  bulkPublish(event) {
    event.preventDefault()
    if (this.selectedIds.length === 0) return

    if (confirm(`Publish ${this.selectedIds.length} item(s)?`)) {
      this.submitBulkAction("publish")
    }
  }

  // Bulk unpublish action
  bulkUnpublish(event) {
    event.preventDefault()
    if (this.selectedIds.length === 0) return

    if (confirm(`Unpublish ${this.selectedIds.length} item(s)?`)) {
      this.submitBulkAction("unpublish")
    }
  }

  // Bulk delete action
  bulkDelete(event) {
    event.preventDefault()
    if (this.selectedIds.length === 0) return

    if (confirm(`Delete ${this.selectedIds.length} item(s)? This cannot be undone.`)) {
      this.submitBulkAction("delete")
    }
  }

  // Submit bulk action via form
  submitBulkAction(action) {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.submitUrlValue || "/admin/contents/bulk"

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement("input")
      csrfInput.type = "hidden"
      csrfInput.name = "authenticity_token"
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    // Add action type
    const actionInput = document.createElement("input")
    actionInput.type = "hidden"
    actionInput.name = "bulk_action"
    actionInput.value = action
    form.appendChild(actionInput)

    // Add selected IDs
    this.selectedIds.forEach(id => {
      const idInput = document.createElement("input")
      idInput.type = "hidden"
      idInput.name = "ids[]"
      idInput.value = id
      form.appendChild(idInput)
    })

    document.body.appendChild(form)
    form.submit()
  }

  // Clear all selections
  clearSelection() {
    this.checkboxTargets.forEach(cb => {
      cb.checked = false
      this.updateRowHighlight(cb)
    })
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }
    this.updateBulkActionsVisibility()
  }
}
